"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { updateDropInCachedDrops } from "@/components/react-query-wrapper/utils/updateAttachmentInCachedDrops";
import { ProcessIncomingDropType } from "@/contexts/wave/hooks/useWaveRealtimeUpdater";
import { useMyStreamOptional } from "@/contexts/wave/MyStreamContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropPoll } from "@/generated/models/ApiDropPoll";
import { preserveAuthenticatedPollVote } from "@/helpers/waves/poll-vote-reconciliation";
import { voteDropPollV2 } from "@/services/api/wave-drops-v2-api";
import { CheckIcon } from "@heroicons/react/24/outline";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type SyntheticEvent, useCallback, useState } from "react";
import { PollOptionVoters } from "./PollOptionVoters";
import { PollResultOption } from "./PollResultOption";
import { PollVoteOption } from "./PollVoteOption";
import { getOptimisticPoll, getPollClosingLabel, getPollSubmitLabel } from "./WaveDropPoll.helpers";
type PollView = "vote" | "results";
interface WaveDropPollProps {
  readonly drop: ApiDrop;
}
interface LocalPollOverride {
  readonly sourcePoll: ApiDropPoll | undefined;
  readonly poll: ApiDropPoll | null;
}
interface PollInteractionState {
  readonly pollKey: string;
  readonly view: PollView;
  readonly selectedOptionNos: readonly number[];
  readonly expandedOptionNo: number | null;
}
const getDefaultPollView = (poll: ApiDropPoll): PollView => poll.is_open && poll.voted.length === 0 ? "vote" : "results";
const getVotedKey = (poll: ApiDropPoll): string => [...poll.voted].sort((a, b) => a - b).join(",");
const getPollInteractionKey = (poll: ApiDropPoll): string => `${poll.id}:${poll.is_open ? "open" : "closed"}:${getVotedKey(poll)}`;
const getDefaultPollInteractionState = (poll: ApiDropPoll): PollInteractionState => ({
  pollKey: getPollInteractionKey(poll),
  view: getDefaultPollView(poll),
  selectedOptionNos: [...poll.voted],
  expandedOptionNo: null
});
const getScopedPollInteractionState = (current: PollInteractionState | null, poll: ApiDropPoll): PollInteractionState => {
  const pollKey = getPollInteractionKey(poll);
  return current?.pollKey === pollKey ? current : getDefaultPollInteractionState(poll);
};
const stopPollEventPropagation = (event: SyntheticEvent) => {
  event.stopPropagation();
};
const getLocalPoll = (localPollOverride: LocalPollOverride | null, sourcePoll: ApiDropPoll | undefined): ApiDropPoll | null => {
  if (!localPollOverride) {
    return null;
  }
  if (localPollOverride.sourcePoll === sourcePoll) {
    return localPollOverride.poll;
  }
  return preserveAuthenticatedPollVote(sourcePoll, localPollOverride.poll, {
    preferExistingVote: true
  }) ?? null;
};
export default function WaveDropPoll(t0) {
  const $ = _c(76);
  const {
    drop
  } = t0;
  const queryClient = useQueryClient();
  const {
    requestAuth,
    setToast
  } = useAuth();
  const myStream = useMyStreamOptional();
  const [localPollOverride, setLocalPollOverride] = useState(null);
  let t1;
  if ($[0] !== drop.poll || $[1] !== localPollOverride) {
    t1 = getLocalPoll(localPollOverride, drop.poll);
    $[0] = drop.poll;
    $[1] = localPollOverride;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const localPoll = t1;
  const poll = localPoll ?? drop.poll;
  const [interactionState, setInteractionState] = useState(null);
  let t2;
  if ($[3] !== drop) {
    t2 = async options => await voteDropPollV2({
      drop,
      options
    });
    $[3] = drop;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== drop.poll || $[6] !== myStream || $[7] !== queryClient) {
    t3 = updatedDrop => {
      if (updatedDrop.poll) {
        setLocalPollOverride({
          sourcePoll: drop.poll,
          poll: updatedDrop.poll
        });
        setInteractionState({
          ...getDefaultPollInteractionState(updatedDrop.poll),
          view: "results"
        });
      }
      updateDropInCachedDrops(queryClient, updatedDrop);
      myStream?.processIncomingDrop(updatedDrop, ProcessIncomingDropType.DROP_INSERT);
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DROP_POLL_VOTERS]
      }).catch(_temp);
    };
    $[5] = drop.poll;
    $[6] = myStream;
    $[7] = queryClient;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== setToast) {
    t4 = error => {
      setToast({
        message: error instanceof Error ? error.message : String(error),
        type: "error"
      });
    };
    $[9] = setToast;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = {
      mutationFn: t2,
      onSuccess: t3,
      onError: t4
    };
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const voteMutation = useMutation(t5);
  let t6;
  if ($[15] !== drop.poll || $[16] !== interactionState || $[17] !== localPollOverride || $[18] !== poll || $[19] !== requestAuth || $[20] !== voteMutation) {
    t6 = async selectedOptionNos => {
      if (!poll?.is_open || selectedOptionNos.length === 0) {
        return false;
      }
      const {
        success
      } = await requestAuth();
      if (!success) {
        return false;
      }
      const previousLocalPollOverride = localPollOverride;
      const previousInteractionState = interactionState;
      const optimisticPoll = getOptimisticPoll(poll, selectedOptionNos);
      setLocalPollOverride({
        sourcePoll: drop.poll,
        poll: optimisticPoll
      });
      setInteractionState({
        ...getDefaultPollInteractionState(optimisticPoll),
        view: "results"
      });
      voteMutation.mutate(selectedOptionNos, {
        onError: () => {
          setLocalPollOverride(previousLocalPollOverride);
          setInteractionState(previousInteractionState);
        }
      });
      return true;
    };
    $[15] = drop.poll;
    $[16] = interactionState;
    $[17] = localPollOverride;
    $[18] = poll;
    $[19] = requestAuth;
    $[20] = voteMutation;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const submitPollVote = t6;
  let t7;
  if ($[22] !== poll || $[23] !== submitPollVote) {
    t7 = optionNo => {
      if (!poll) {
        return;
      }
      if (!poll.multichoice) {
        setInteractionState(current => {
          const scopedState = getScopedPollInteractionState(current, poll);
          return {
            ...scopedState,
            selectedOptionNos: [optionNo]
          };
        });
        submitPollVote([optionNo]).then(submitted => {
          if (submitted) {
            return;
          }
          setInteractionState(current_0 => {
            const scopedState_0 = getScopedPollInteractionState(current_0, poll);
            if (scopedState_0.selectedOptionNos.length !== 1 || scopedState_0.selectedOptionNos[0] !== optionNo) {
              return scopedState_0;
            }
            return {
              ...scopedState_0,
              selectedOptionNos: [...poll.voted]
            };
          });
        }).catch(_temp2);
        return;
      }
      setInteractionState(current_1 => {
        const scopedState_1 = getScopedPollInteractionState(current_1, poll);
        const next = new Set(scopedState_1.selectedOptionNos);
        if (next.has(optionNo)) {
          next.delete(optionNo);
        } else {
          next.add(optionNo);
        }
        return {
          ...scopedState_1,
          selectedOptionNos: [...next].sort(_temp3)
        };
      });
    };
    $[22] = poll;
    $[23] = submitPollVote;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const handleVoteOptionChange = t7;
  let t8;
  if ($[25] !== interactionState || $[26] !== poll || $[27] !== submitPollVote) {
    t8 = async () => {
      if (!poll?.is_open) {
        return;
      }
      const scopedState_2 = getScopedPollInteractionState(interactionState, poll);
      if (scopedState_2.selectedOptionNos.length === 0) {
        return;
      }
      await submitPollVote(scopedState_2.selectedOptionNos);
    };
    $[25] = interactionState;
    $[26] = poll;
    $[27] = submitPollVote;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const handleSubmitVote = t8;
  let t9;
  if ($[29] !== poll) {
    t9 = () => {
      if (!poll?.is_open) {
        return;
      }
      setInteractionState(current_2 => {
        const scopedState_3 = getScopedPollInteractionState(current_2, poll);
        return {
          ...scopedState_3,
          view: "vote",
          selectedOptionNos: [...poll.voted],
          expandedOptionNo: null
        };
      });
    };
    $[29] = poll;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const showVoteView = t9;
  if (!poll) {
    return null;
  }
  const hasVoted = poll.voted.length > 0;
  let scopedState_4;
  let t10;
  if ($[31] !== interactionState || $[32] !== poll) {
    scopedState_4 = getScopedPollInteractionState(interactionState, poll);
    t10 = new Set(scopedState_4.selectedOptionNos);
    $[31] = interactionState;
    $[32] = poll;
    $[33] = scopedState_4;
    $[34] = t10;
  } else {
    scopedState_4 = $[33];
    t10 = $[34];
  }
  const selectedOptions = t10;
  let t11;
  if ($[35] !== poll.options) {
    t11 = poll.options.reduce(_temp4, 0);
    $[35] = poll.options;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  const totalVotes = t11;
  let t12;
  if ($[37] !== poll.voted) {
    t12 = new Set(poll.voted);
    $[37] = poll.voted;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  const votedOptionNos = t12;
  const showSelectionIndicator = votedOptionNos.size > 0;
  const canShowResults = !poll.is_open || hasVoted;
  const effectiveView = canShowResults && (!poll.is_open || scopedState_4.view === "results") ? "results" : "vote";
  const expandedOptionNo = scopedState_4.expandedOptionNo;
  const voteOptionGroupName = `drop-poll-${poll.id}`;
  let t13;
  if ($[39] !== hasVoted || $[40] !== voteMutation.isPending) {
    t13 = getPollSubmitLabel({
      isPending: voteMutation.isPending,
      hasVoted
    });
    $[39] = hasVoted;
    $[40] = voteMutation.isPending;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  const submitButtonLabel = t13;
  const showResultsFooterActions = effectiveView === "results" && hasVoted;
  const showFooterAction = poll.is_open && showResultsFooterActions;
  const showMultichoiceSubmit = poll.multichoice && selectedOptions.size > 0;
  let t14;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="tw-text-[15px] tw-font-bold tw-text-white">Poll</div>;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const t15 = `tw-size-1.5 tw-flex-shrink-0 tw-rounded-full ${poll.is_open ? "tw-bg-green" : "tw-bg-iron-600"}`;
  let t16;
  if ($[43] !== t15) {
    t16 = <span className={t15} aria-hidden="true" />;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== poll) {
    t17 = getPollClosingLabel(poll);
    $[45] = poll;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== t17) {
    t18 = <span className="tw-truncate">{t17}</span>;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t16 || $[50] !== t18) {
    t19 = <div className="tw-mb-3.5 tw-flex tw-flex-wrap tw-items-center tw-gap-3">{t14}<div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-text-xs tw-font-medium tw-text-iron-400">{t16}{t18}</div></div>;
    $[49] = t16;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== drop.id || $[53] !== effectiveView || $[54] !== expandedOptionNo || $[55] !== handleSubmitVote || $[56] !== handleVoteOptionChange || $[57] !== hasVoted || $[58] !== poll || $[59] !== selectedOptions || $[60] !== showMultichoiceSubmit || $[61] !== showSelectionIndicator || $[62] !== submitButtonLabel || $[63] !== totalVotes || $[64] !== voteMutation.isPending || $[65] !== voteOptionGroupName || $[66] !== votedOptionNos) {
    t20 = effectiveView === "vote" && poll.is_open ? <div className="tw-flex tw-flex-col tw-gap-2">{poll.options.map(option_0 => <PollVoteOption key={option_0.option_no} option={option_0} checked={selectedOptions.has(option_0.option_no)} multichoice={poll.multichoice} groupName={voteOptionGroupName} disabled={voteMutation.isPending} onChange={handleVoteOptionChange} />)}{poll.multichoice && <div className={`tw-grid tw-transition-all tw-duration-300 tw-ease-out ${showMultichoiceSubmit ? "tw-mt-3.5 tw-grid-rows-[1fr] tw-opacity-100" : "tw-mt-0 tw-grid-rows-[0fr] tw-opacity-0"}`}><div aria-hidden={!showMultichoiceSubmit} inert={!showMultichoiceSubmit} className="tw-overflow-hidden"><button type="button" disabled={!showMultichoiceSubmit || voteMutation.isPending} onClick={event => {
            event.stopPropagation();
            handleSubmitVote().catch(_temp5);
          }} className="tw-flex tw-w-full tw-transform-gpu tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-white tw-bg-white tw-px-4 tw-py-2 tw-text-[13.5px] tw-font-bold tw-text-black tw-transition-all tw-duration-300 disabled:tw-cursor-not-allowed disabled:tw-border-white/[0.06] disabled:tw-bg-white/[0.025] disabled:tw-text-iron-500 desktop-hover:hover:tw-bg-iron-100">{submitButtonLabel}</button></div></div>}</div> : <div className="tw-flex tw-flex-col tw-gap-2">{poll.options.map(option_1 => {
        const isExpanded = expandedOptionNo === option_1.option_no;
        return <div key={option_1.option_no}><PollResultOption dropId={drop.id} option={option_1} totalVotes={totalVotes} isSelected={votedOptionNos.has(option_1.option_no)} isDimmed={hasVoted && !votedOptionNos.has(option_1.option_no)} isExpanded={isExpanded} multichoice={poll.multichoice} showSelectionIndicator={showSelectionIndicator} onToggle={optionNo_0 => setInteractionState(current_3 => {
            const currentState = getScopedPollInteractionState(current_3, poll);
            return {
              ...currentState,
              expandedOptionNo: currentState.expandedOptionNo === optionNo_0 ? null : optionNo_0
            };
          })} /><div className={`tw-grid tw-transition-all tw-duration-300 tw-ease-out ${isExpanded ? "tw-mb-1 tw-mt-1 tw-grid-rows-[1fr] tw-opacity-100" : "tw-mb-0 tw-mt-0 tw-grid-rows-[0fr] tw-opacity-0"}`}><div aria-hidden={!isExpanded} inert={!isExpanded} className="tw-overflow-hidden"><div className="tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-900 tw-px-3 tw-py-2.5"><PollOptionVoters dropId={drop.id} option={option_1} enabled={isExpanded} /></div></div></div></div>;
      })}</div>;
    $[52] = drop.id;
    $[53] = effectiveView;
    $[54] = expandedOptionNo;
    $[55] = handleSubmitVote;
    $[56] = handleVoteOptionChange;
    $[57] = hasVoted;
    $[58] = poll;
    $[59] = selectedOptions;
    $[60] = showMultichoiceSubmit;
    $[61] = showSelectionIndicator;
    $[62] = submitButtonLabel;
    $[63] = totalVotes;
    $[64] = voteMutation.isPending;
    $[65] = voteOptionGroupName;
    $[66] = votedOptionNos;
    $[67] = t20;
  } else {
    t20 = $[67];
  }
  let t21;
  if ($[68] !== showFooterAction || $[69] !== showVoteView || $[70] !== voteMutation.isPending) {
    t21 = showFooterAction && <div className="tw-mt-3.5 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/[0.06] tw-pt-3.5"><div className="tw-flex tw-items-center tw-justify-between tw-gap-2"><button type="button" disabled={voteMutation.isPending} onClick={event_0 => {
          event_0.stopPropagation();
          showVoteView();
        }} className="tw-flex tw-flex-shrink-0 tw-items-center tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-transparent tw-px-3 tw-py-1.5 tw-text-[13px] tw-font-medium tw-text-iron-300 tw-transition-all disabled:tw-cursor-not-allowed disabled:tw-opacity-50 desktop-hover:hover:tw-border-white/25 desktop-hover:hover:tw-text-white">Change vote</button><span className="tw-flex tw-h-8 tw-flex-shrink-0 tw-items-center tw-gap-1.5 tw-rounded-lg tw-bg-white/[0.06] tw-px-3.5 tw-text-[13px] tw-font-bold tw-text-white tw-opacity-100 tw-transition-opacity tw-duration-500"><span>Voted</span><CheckIcon className="tw-size-3.5 tw-text-green" strokeWidth={3} aria-hidden="true" /></span></div></div>;
    $[68] = showFooterAction;
    $[69] = showVoteView;
    $[70] = voteMutation.isPending;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  let t22;
  if ($[72] !== t19 || $[73] !== t20 || $[74] !== t21) {
    t22 = <div role="presentation" className="tw-mb-2 tw-mt-3 tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-iron-900/80 tw-p-4 tw-shadow-lg tw-backdrop-blur md:tw-max-w-2xl" onClick={stopPollEventPropagation} onKeyDown={stopPollEventPropagation}>{t19}{t20}{t21}</div>;
    $[72] = t19;
    $[73] = t20;
    $[74] = t21;
    $[75] = t22;
  } else {
    t22 = $[75];
  }
  return t22;
}
function _temp5() {}
function _temp4(total, option) {
  return total + option.votes;
}
function _temp3(a, b) {
  return a - b;
}
function _temp2() {}
function _temp() {}